import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { CreditCard } from 'lucide-react'
import { useState } from 'react'
import { Slider } from '@/components/ui/slider'
import { toast } from 'sonner'

export const Route = createFileRoute('/_protected/billing')({
  component: BillingPage,
})

function BillingPage() {
  const { currentUser } = Route.useRouteContext()
  const credits = currentUser?.prefs?.credits || 0
  const userPlan = currentUser?.prefs?.plan || 'starter'

  const planNames: Record<string, string> = {
    starter: 'Starter',
    pro: 'Pro',
    business: 'Business',
    enterprise: 'Enterprise',
  }


  const getMonthlyAllowance = () => {
    switch (userPlan.toLowerCase()) {
      case 'pro':
        return 4500
      case 'business':
        return 10000
      case 'enterprise':
        return 25000
      default:
        return 1500
    }
  }

  const getPlanPrice = () => {
    switch (userPlan.toLowerCase()) {
      case 'pro':
        return 59
      case 'business':
        return 149
      case 'enterprise':
        return 499
      default:
        return 29
    }
  }

  const used = getMonthlyAllowance() - credits
  const monthlyAllowance = getMonthlyAllowance()
  const percent = Math.min(100, (used / monthlyAllowance) * 100)

  // Custom top-up state ($1 = 52 credits)
  const [selectedAmount, setSelectedAmount] = useState(52)

  const creditsFromDollars = (dollars: number) => Math.floor(dollars * 52)
  const dollarsFromCredits = (credits: number) => Math.ceil(credits / 52)

  const handleCustomCheckout = async () => {
    try {
      const res = await fetch(`/topup?credits=${selectedAmount}`, {
        headers: {
          'Content-Type': 'application/json',
        }
      })
      const data = await res.json()
      if (data.url) {
        window.location.href = data.url
      }
    } catch (error) {
      console.log('Failed to create checkout session', error);
      toast.error("Failed to create checkout session")
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto max-w-6xl px-6 py-10">
        {/* Current Balance Card */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <CreditCard className="w-32 h-32 text-primary" />
            </div>
            <div className="flex flex-wrap items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Current Plan
                </p>
                <h2 className="text-2xl font-bold text-foreground capitalize">
                  {planNames[userPlan.toLowerCase()] || userPlan}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  ${getPlanPrice()}/month
                </p>
              </div>
            </div>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-5xl font-bold text-foreground">
                {credits.toLocaleString()}
              </span>
              <span className="text-lg text-muted-foreground">Credits</span>
            </div>
            <div className="mb-2">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-muted-foreground">Monthly Usage</span>
                <span className="text-muted-foreground">
                  {percent.toFixed(0)}%
                </span>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${percent >= 90 ? `bg-red-500` : `bg-primary`}`}
                  style={{ width: `${percent}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {used.toLocaleString()} used / {monthlyAllowance.toLocaleString()} monthly allowance
              </p>
            </div>
          </div>

          {/* Custom Top-up Card */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4">Top Up Credits</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Select amount ($1 = 52 credits)
                </label>
                <Slider
                  min={1}
                  max={100}
                  step={1}
                  onValueChange={(value) => setSelectedAmount(creditsFromDollars(Number(value[0])))}
                  className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>$1 (52 credits)</span>
                  <span>$100 (5,200 credits)</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">You will receive</p>
                  <p className="text-2xl font-bold">{selectedAmount.toLocaleString()} credits</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Price</p>
                  <p className="text-2xl font-bold">${dollarsFromCredits(selectedAmount)}</p>
                </div>
              </div>
              <Button className="w-full" onClick={handleCustomCheckout}>
                Proceed to Checkout
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
